import * as i0 from '@angular/core';
import { inject, Injectable, Component, NgModule } from '@angular/core';
import { BehaviorSubject, throwError, of, from, defer, tap as tap$1, switchMap as switchMap$1, map as map$1, Subscription } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { isPast } from 'date-fns';
import jwt_decode from 'jwt-decode';
import * as i1$1 from '@angular/router';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { fetchAuthSession, signInWithRedirect } from '@aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { signOut } from 'aws-amplify/auth';
import { NgxPermissionsService } from 'ngx-permissions';
import * as i1 from '@angular/common';
import { Location, CommonModule, LocationStrategy } from '@angular/common';

class TaskPermission {
    get taskPermissionAsString() {
        return `${this.task}/${this.permission}`;
    }
    constructor(task, permission) {
        this.task = task;
        this.permission = permission;
    }
}

var Permission;
(function (Permission) {
    Permission["Create"] = "C";
    Permission["Delete"] = "D";
    Permission["Export"] = "E";
    Permission["Import"] = "I";
    Permission["Print"] = "P";
    Permission["Read"] = "R";
    Permission["Search"] = "S";
    Permission["Update"] = "U";
    Permission["Execute"] = "X";
})(Permission || (Permission = {}));

class Gebruiker {
    constructor(gebruiker) {
        this.rijksregisternummer = gebruiker.rijksregisternummer;
        this.voornaam = gebruiker.voornaam;
        this.achternaam = gebruiker.achternaam;
        this.email = gebruiker.email;
        this.doelgroepCode = gebruiker.doelgroepCode;
        this.doelgroepNaam = gebruiker.doelgroepNaam;
        this.isWettelijkVertegenwoordiger = gebruiker.isWettelijkVertegenwoordiger;
        this.voId = gebruiker.voId;
        this.organisatieCode = gebruiker.organisatieCode;
        this.organisatieNaam = gebruiker.organisatieNaam;
        this.rollen = gebruiker.rollen;
    }
    /** naam onder formaat 'Voornaam Achternaam' */
    get volledigeNaam() {
        return `${this.voornaam} ${this.achternaam}`.trim();
    }
}

var Doelgroepen;
(function (Doelgroepen) {
    /** Burger */
    Doelgroepen["BUR"] = "BUR";
    /** Economisch Actoren */
    Doelgroepen["EA"] = "EA";
    /** Medewerker Vlaamse Overheid */
    Doelgroepen["GID"] = "GID";
    /** Lokale Besturen */
    Doelgroepen["LB"] = "LB";
    /** Onderwijs en Vorming */
    Doelgroepen["OV"] = "OV";
})(Doelgroepen || (Doelgroepen = {}));

class ConfigurationService {
}

/** Abstract authentication service class */
class AnbAuthenticationService {
    constructor({ tokenKey = 'anb::token', policiesKey = 'anb::policies', redirectKey = 'anb::origin' } = {}) {
        this.#isAuthenticatedSrc = new BehaviorSubject(false);
        this.isAuthenticated$ = this.#isAuthenticatedSrc.asObservable();
        this.tokenKey = tokenKey;
        this.policiesKey = policiesKey;
        this.redirectKey = redirectKey;
    }
    #isAuthenticatedSrc;
    set keySuffix(value) {
        this.tokenKey = `${this.tokenKey}::${value}`;
        this.policiesKey = `${this.policiesKey}::${value}`;
        if (this.redirectKey)
            this.redirectKey = `${this.redirectKey}::${value}`;
    }
    /** Returns the taskpermissions as an array of strings from the local storage. */
    get policies() {
        const policies = JSON.parse(localStorage.getItem(this.policiesKey));
        return policies;
    }
    /** Set the taskpermissions as an array of strings in the local storage. */
    set policies(policies) {
        if (!policies) {
            localStorage.removeItem(this.policiesKey);
        }
        else {
            localStorage.setItem(this.policiesKey, JSON.stringify(policies));
        }
    }
    /** Returns the url before the redirect to ADFS. */
    get redirectUrl() {
        return localStorage.getItem(this.redirectKey);
    }
    /** Set the url before the redirect to ADFS. */
    set redirectUrl(originUrl) {
        if (!originUrl) {
            localStorage.removeItem(this.redirectKey);
        }
        else {
            localStorage.setItem(this.redirectKey, originUrl);
        }
    }
    /** Returns the current JWT token. */
    get token() {
        return localStorage.getItem(this.tokenKey);
    }
    /** Set a JWT token. */
    set token(token) {
        if (!token) {
            localStorage.removeItem(this.tokenKey);
        }
        else {
            localStorage.setItem(this.tokenKey, token);
        }
    }
    /** Returns true when there is a token and the current token is not expired. */
    get hasAuthenticatedSession() {
        const isValid = !!this.token && !this.isTokenExpired;
        if (this.#isAuthenticatedSrc.value !== isValid) {
            this.#isAuthenticatedSrc.next(isValid);
        }
        return isValid;
    }
    /** Clear the saved authentication information. **/
    clear() {
        const authKeys = [this.tokenKey, this.redirectKey, this.policiesKey];
        authKeys.forEach((key) => {
            if (key)
                localStorage.removeItem(key);
        });
    }
    /** Returns the expiration date of the current token. */
    get tokenExpiryDate() {
        const decoded = jwt_decode(this.token);
        if (!decoded?.exp) {
            return undefined;
        }
        const date = new Date(0);
        date.setUTCSeconds(decoded.exp);
        return date;
    }
    /** Returns true when the current token is expired. */
    get isTokenExpired() {
        if (!this.token) {
            return true;
        }
        const date = this.tokenExpiryDate;
        if (date === undefined) {
            return false;
        }
        return isPast(date);
    }
}

class AnbUnauthorizedHttpInterceptor {
    constructor() {
        this.authService = inject(AnbAuthenticationService);
    }
    intercept(request, next) {
        return next.handle(request).pipe(map((event) => {
            return event;
        }), catchError((error) => {
            if (error.status === 401) {
                this.authService.clear();
                this.authService.redirectToLogin();
            }
            return throwError(() => error);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUnauthorizedHttpInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUnauthorizedHttpInterceptor, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUnauthorizedHttpInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class AnbAuthTokenHttpInterceptor {
    constructor() {
        this.authService = inject(AnbAuthenticationService);
        this.shouldSkip = (path) => {
            const { configuration } = this.authService;
            const hasExcluded = configuration.excludeUrls?.some((exclude) => new RegExp(exclude).test(path)) ?? false;
            const hasIncluded = configuration.includeUrls?.some((include) => new RegExp(include).test(path)) ?? false;
            return hasExcluded || !hasIncluded;
        };
        this.intercept = (request, next) => {
            const shouldAttachToken = !this.shouldSkip(request.url);
            if (!shouldAttachToken) {
                return next.handle(request);
            }
            return this.attachToken(request, next);
        };
        this.attachToken = (request, next) => this.authService.getToken().pipe(switchMap((token) => {
            if (token) {
                request = request.clone({ headers: request.headers.set('Authorization', `Bearer ${token}`) });
            }
            return next.handle(request);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbAuthTokenHttpInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbAuthTokenHttpInterceptor, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbAuthTokenHttpInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class AuthConfiguration {
    constructor() {
        /** Urls that will get the JWT attached. */
        this.includeUrls = [];
        /** Urls that will not get JWT attached. */
        this.excludeUrls = [];
    }
}

class CognitoConfiguration extends AuthConfiguration {
}

class CognitoApiAuthClient {
    constructor() {
        this.http = inject(HttpClient);
        this.configuration = inject(CognitoConfiguration);
        this.getPolicies = () => this.http.get(this.configuration.policiesUrl);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoApiAuthClient, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoApiAuthClient }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoApiAuthClient, decorators: [{
            type: Injectable
        }] });

class CognitoGebruikerMapper {
    mapTokenToGebruiker(token) {
        const payload = token.payload;
        const rijksregisternummer = payload['custom:rrn'];
        const achternaam = payload['family_name'];
        const voornaam = payload['given_name'];
        const doelgroepcode = payload['custom:vo_doelgroep_code'];
        const doelgroepNaam = payload['custom:vo_doelgroep_naam'];
        const voId = payload['custom:vo_id'];
        const organisatieCode = payload['custom:vo_organisatie_code'];
        const organisatieNaam = payload['custom:vo_organisatie_naam'];
        const email = this.removeOrganisationSuffix(payload['custom:vo_email']);
        const rollen = this.normalizeRollen(payload['custom:acm_rollen']);
        const wetVer = payload['custom:wv_code'] ?? '';
        const isWettelijkVertegenwoordiger = !!wetVer && wetVer.startsWith('WettelijkVertegenwoordiger');
        return new Gebruiker({
            achternaam: achternaam,
            voornaam: voornaam,
            rijksregisternummer: rijksregisternummer,
            email: email,
            voId: voId,
            organisatieCode: organisatieCode,
            organisatieNaam: organisatieNaam,
            rollen: rollen,
            isWettelijkVertegenwoordiger: isWettelijkVertegenwoordiger,
            doelgroepCode: doelgroepcode,
            doelgroepNaam: doelgroepNaam,
        });
    }
    removeOrganisationSuffix(value) {
        if (!value)
            return value;
        return value.split(':')[0];
    }
    removeApplicationPrefix(value) {
        if (!value)
            return value;
        return value.split('-')[1];
    }
    stringToArray(value) {
        if (!value)
            return [];
        if (Array.isArray(value)) {
            return value;
        }
        else {
            return [value];
        }
    }
    normalizeRollen(rollen) {
        return this.stringToArray(rollen).map((rol) => this.removeOrganisationSuffix(this.removeApplicationPrefix(rol)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoGebruikerMapper, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoGebruikerMapper, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoGebruikerMapper, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class CognitoAuthService {
    constructor() {
        this.router = inject(Router);
        this.configuration = inject(CognitoConfiguration);
        this.apiAuthClient = inject(CognitoApiAuthClient);
        this.gebruikerMapper = inject(CognitoGebruikerMapper);
        this.keySuffix = !!this.configuration.appName ? `::${this.configuration.appName}` : '';
        this.redirectKey = `anb::origin${this.keySuffix}`;
        this.policiesKey = `anb::policies${this.keySuffix}`;
        this.idToken = new BehaviorSubject(null);
        this.accessToken = new BehaviorSubject(null);
        Amplify.configure({ Auth: this.configuration.cognito });
    }
    /** Returns the security policies for the logged in user. Ex. ['PARTIJ/U', 'PARTIJ/C'] **/
    getSecurityPolicies() {
        if (!this.policies?.length) {
            return this.apiAuthClient.getPolicies().pipe(tap((policies) => {
                this.policies = policies;
            }));
        }
        return of(this.policies);
    }
    clear() {
        const authKeys = [this.redirectKey, this.policiesKey];
        authKeys.forEach((key) => {
            if (key)
                localStorage.removeItem(key);
        });
        this.accessToken.next(null);
        this.idToken.next(null);
    }
    get hasAuthenticatedSession() {
        return from(this.isAuthenticated());
    }
    async isAuthenticated() {
        const token = await this.loadTokens();
        return !!token;
    }
    async loadTokens() {
        try {
            const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};
            this.idToken.next(idToken);
            this.accessToken.next(accessToken);
            return accessToken;
        }
        catch (err) {
            console.log(err);
        }
    }
    /**
     * If the current JWT token in storage is still recent enough return it.
     * If the token is not recent but still valid refresh it without need for user consent.
     */
    async getToken() {
        await this.isAuthenticated();
        return this.idToken.value || null;
    }
    /**
     * Login and save the redirect url in localstorage, after redirecting the guard will redirect back to this url
     */
    login(redirectUrl = undefined) {
        this.redirectUrl = redirectUrl;
        signInWithRedirect({ provider: { custom: this.configuration.cognitoFederatedIdentityProviderName } })
            .then((_) => { })
            .catch((err) => {
            console.log('getLoginUrlWithRedirect: ');
            console.log(err);
        });
    }
    logout() {
        signOut();
    }
    /** Returns the taskpermissions as an array of strings from the local storage. */
    get policies() {
        const policies = JSON.parse(localStorage.getItem(this.policiesKey));
        return policies;
    }
    /** Set the taskpermissions as an array of strings in the local storage. */
    set policies(policies) {
        if (!policies) {
            localStorage.removeItem(this.policiesKey);
        }
        else {
            localStorage.setItem(this.policiesKey, JSON.stringify(policies));
        }
    }
    /** Returns the url before the redirect to Cognito. */
    get redirectUrl() {
        return localStorage.getItem(this.redirectKey);
    }
    /** Set the url before the redirect to Cognito. */
    set redirectUrl(originUrl) {
        if (!originUrl) {
            localStorage.removeItem(this.redirectKey);
        }
        else {
            localStorage.setItem(this.redirectKey, originUrl);
        }
    }
    /** Returns the fullname of the current user. */
    get principalName() {
        if (this.idToken) {
            const token = this.idToken.getValue();
            const voornaam = token?.payload['given_name'];
            const naam = token?.payload['family_name'];
            if (voornaam && naam) {
                return `${voornaam} ${naam}`;
            }
        }
        return null;
    }
    /** Returns the voId of the current user. */
    get voId() {
        if (this.idToken) {
            const voId = this.idToken.getValue()?.payload['custom:vo_id'];
            return voId.toString();
        }
        return null;
    }
    /** Returns human readable information about the current logged in user, provided there is an autthenticated session*/
    get gebruiker() {
        if (this.hasAuthenticatedSession) {
            return this.gebruikerMapper.mapTokenToGebruiker(this.idToken.getValue());
        }
        return null;
    }
    redirectToLogin(path = '') {
        this.router.navigate([path]);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoAuthService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoAuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoAuthService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class AnbCognitoAuthTokenHttpInterceptor {
    constructor() {
        this.authService = inject(CognitoAuthService);
        this.shouldSkip = (path) => {
            const { configuration } = this.authService;
            const hasExcluded = configuration.excludeUrls?.some((exclude) => new RegExp(exclude).test(path)) ?? false;
            const hasIncluded = configuration.includeUrls?.some((include) => new RegExp(include).test(path)) ?? false;
            return hasExcluded || !hasIncluded;
        };
        this.intercept = (request, next) => {
            const shouldAttachToken = !this.shouldSkip(request.url);
            if (!shouldAttachToken) {
                return next.handle(request);
            }
            return this.attachToken(request, next);
        };
        this.attachToken = (request, next) => {
            return from(this.authService.getToken()).pipe(switchMap((token) => {
                if (token) {
                    request = request.clone({ headers: request.headers.set('Authorization', `Bearer ${token}`) });
                }
                return next.handle(request);
            }));
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbCognitoAuthTokenHttpInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbCognitoAuthTokenHttpInterceptor, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbCognitoAuthTokenHttpInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class GuardBaseService {
    constructor() {
        this.router = inject(Router);
        this.permissionsService = inject(NgxPermissionsService);
        this.authService = inject(AnbAuthenticationService);
        this.requiredPermissions = (route) => {
            let permissions = [];
            if (route?.data?.taskPermission instanceof TaskPermission) {
                permissions.push(route.data.taskPermission);
            }
            if (route?.data?.taskPermissions instanceof Array && route?.data?.taskPermissions?.every((x) => x instanceof TaskPermission)) {
                permissions = permissions.concat(route.data.taskPermissions);
            }
            return permissions;
        };
        /** Gets and stores user permissions and required permissions for the route. */
        this.getPermissions$ = (route) => this.authService.getSecurityPolicies().pipe(map((policies) => {
            this.permissionsService.loadPermissions(policies);
            return this.requiredPermissions(route);
        }), catchError((error) => {
            throwError(() => error);
            return of([]);
        }));
        this.hasPermission$ = (permissions, allowByDefault = true) => defer(() => {
            if (!permissions?.length) {
                return of(allowByDefault);
            }
            const permissionsString = permissions.map((p) => p.taskPermissionAsString);
            return this.permissionsService.hasPermission(permissionsString);
        });
    }
}

class AnbAuthGuardService extends GuardBaseService {
    canActivate(route, state) {
        const { configuration } = this.authService;
        if (!this.authService.hasAuthenticatedSession) {
            this.authService.redirectToLogin(state.url);
            return false;
        }
        return this.getPermissions$(route).pipe(switchMap((permissions) => this.hasPermission$(permissions).pipe(map((authorised) => {
            if (!authorised) {
                const deniedPath = route.data?.redirectAccessDeniedTo ?? configuration.accessDeniedPath;
                this.router.navigateByUrl(deniedPath, { skipLocationChange: true });
                return false;
            }
            return true;
        }))));
    }
}
const AnbAuthGuard = (next, state) => inject(AnbAuthGuardService).canActivate(next, state);

class AnbModuleAuthGuardService extends GuardBaseService {
    canMatch(route, segments) {
        const { configuration } = this.authService;
        const path = segments.join('/');
        if (!this.authService.hasAuthenticatedSession) {
            this.authService.redirectToLogin(path);
            return false;
        }
        return this.getPermissions$(route).pipe(switchMap((permissions) => this.hasPermission$(permissions).pipe(map((authorised) => {
            if (!authorised) {
                const deniedPath = route.data?.redirectAccessDeniedTo ?? configuration.accessDeniedPath;
                this.router.navigateByUrl(deniedPath);
                return false;
            }
            return true;
        }))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbModuleAuthGuardService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbModuleAuthGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbModuleAuthGuardService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
const AnbModuleAuthGuard = (route, segments) => inject(AnbModuleAuthGuardService).canMatch(route, segments);

class AnbPermissionGuardService {
    constructor() {
        this.permissionsService = inject(NgxPermissionsService);
        this.cognitoAuthService = inject(CognitoAuthService);
        this.requiredPermissions = (permissions) => {
            // Ensure permissions are always treated as an array
            const permissionArray = Array.isArray(permissions) ? permissions : [permissions];
            // Filter out invalid permissions
            const validPermissions = permissionArray.filter((permission) => permission instanceof TaskPermission);
            return validPermissions;
        };
        /** Gets and stores user permissions and required permissions for the route. */
        this.getPermissions$ = (permissions) => this.cognitoAuthService.getSecurityPolicies().pipe(map((policies) => {
            this.permissionsService.loadPermissions(policies);
            return this.requiredPermissions(permissions);
        }), catchError((error) => {
            throwError(() => error);
            return of([]);
        }));
        this.hasPermission$ = (permissions, allowByDefault = true) => defer(() => {
            if (!permissions?.length) {
                return of(allowByDefault);
            }
            const permissionsString = permissions.map((p) => p.taskPermissionAsString);
            return this.permissionsService.hasPermission(permissionsString);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbPermissionGuardService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbPermissionGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbPermissionGuardService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const AnbCognitoAuthGuard = () => {
    const authService = inject(CognitoAuthService);
    return authService.hasAuthenticatedSession.pipe(tap$1((hasSession) => {
        if (!hasSession) {
            authService.login(window.location.href);
        }
        else {
            // Redirect to the original URL if login was successful
            if (authService.redirectUrl && authService.redirectUrl !== window.location.href) {
                window.location.href = authService.redirectUrl;
                authService.redirectUrl = null;
            }
        }
        return hasSession;
    }));
};
/// Functional guard should be defined as such to allow arguments
function AnbCognitoPermissionGuard(permissions, optionalRedirectRoute = null) {
    return (ars, rss) => {
        const guardService = inject(AnbPermissionGuardService);
        const configuration = inject(CognitoConfiguration);
        const router = inject(Router);
        return guardService.getPermissions$(permissions).pipe(switchMap$1((permissions) => guardService.hasPermission$(permissions).pipe(map$1((authorised) => {
            if (!authorised) {
                router.navigateByUrl(optionalRedirectRoute ?? configuration.accessDeniedPath, { skipLocationChange: true });
                return false;
            }
            return true;
        }))));
    };
}

class AdfsConfiguration extends AuthConfiguration {
}

const ROUTE = {
    CLAIMS: 'claims',
    TOKEN: 'token',
    LOGGEDOUT: 'loggedout',
};

class GebruikerMapper {
    removeOrganisationSuffix(value) {
        if (!value)
            return value;
        return value.split(':')[0];
    }
    removeApplicationPrefix(value) {
        if (!value)
            return value;
        return value.split('-')[1];
    }
    stringToArray(value) {
        if (!value)
            return [];
        if (Array.isArray(value)) {
            return value;
        }
        else {
            return [value];
        }
    }
    normalizeRollen(rollen) {
        return this.stringToArray(rollen).map((rol) => this.removeOrganisationSuffix(this.removeApplicationPrefix(rol)));
    }
}

class AdfsGebruikerMapper extends GebruikerMapper {
    mapTokenToGebruiker(token) {
        const rijksregisternummer = token['vo-pn'];
        const voornaam = token['vo-voornaam'];
        const achternaam = token['sn'];
        const doelgroepcode = token['vo-doelgroepcode'];
        const doelgroepNaam = token['vo-doelgroepnaam'];
        const voId = token['vo-idv'];
        const organisatieCode = token['vo-org-code'];
        const organisatieNaam = token['vo-org-naam'];
        let isWettelijkVertegenwoordiger = false;
        let tokenRollen;
        let tokenEmail;
        switch (doelgroepcode) {
            case Doelgroepen.GID: {
                tokenRollen = token['federatie-applicatie60-applicatiedata'];
                tokenEmail = token['federatie-applicatie61-applicatiedata'];
                break;
            }
            case Doelgroepen.EA: {
                tokenRollen = token['federatie-applicatie35-applicatiedata'];
                tokenEmail = token['federatie-applicatie36-applicatiedata'];
                const wetVer = token['vo-wsconsult-orgcode'] ?? '';
                isWettelijkVertegenwoordiger = !!wetVer && wetVer.startsWith('WettelijkVertegenwoordiger');
                break;
            }
            case Doelgroepen.LB: {
                tokenRollen = token['federatie-applicatie32-applicatiedata'];
                tokenEmail = token['federatie-applicatie33-applicatiedata'];
                break;
            }
            case Doelgroepen.OV: {
                tokenRollen = token['federatie-applicatie7-applicatiedata'];
                tokenEmail = token['federatie-applicatie8-applicatiedata'];
                break;
            }
            case Doelgroepen.BUR:
            default:
                break;
        }
        const rollen = this.normalizeRollen(tokenRollen);
        const email = this.removeOrganisationSuffix(tokenEmail);
        return new Gebruiker({
            achternaam: achternaam,
            voornaam: voornaam,
            rijksregisternummer: rijksregisternummer,
            email: email,
            voId: voId,
            organisatieCode: organisatieCode,
            organisatieNaam: organisatieNaam,
            rollen: rollen,
            isWettelijkVertegenwoordiger: isWettelijkVertegenwoordiger,
            doelgroepCode: doelgroepcode,
            doelgroepNaam: doelgroepNaam,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsGebruikerMapper, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsGebruikerMapper, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsGebruikerMapper, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const TOKEN_KEY$1 = 'Authentication::Token';
const POLICIES_KEY$1 = 'Authorization::Policies';
const REDIRECT_KEY = 'Authentication::Redirect';
class AdfsAuthService extends AnbAuthenticationService {
    constructor() {
        super({ tokenKey: TOKEN_KEY$1, policiesKey: POLICIES_KEY$1, redirectKey: REDIRECT_KEY });
        this.applicationConfigurationService = inject(ConfigurationService);
        this.router = inject(Router);
        this.configuration = inject(AdfsConfiguration);
        this.gebruikerMapper = inject(AdfsGebruikerMapper);
    }
    /** Returns the security policies for the logged in user. Ex. ['PARTIJ/U', 'PARTIJ/C'] **/
    getSecurityPolicies() {
        if (!this.policies?.length) {
            return this.applicationConfigurationService.getSecurityPolicies().pipe(tap((policies) => {
                this.policies = policies;
            }));
        }
        return of(this.policies);
    }
    getToken() {
        return of(this.token);
    }
    /** Redirect the user to de ADFS login page. **/
    redirectToLogin(originUrl = '/') {
        this.redirectUrl = originUrl;
        const n = new Date().getTime();
        const { adfsAuthorizeUrl, adfsClientId, adfsRedirectUri } = this.configuration;
        const href = `${adfsAuthorizeUrl}?response_type=code&client_id=${adfsClientId}&resource=${adfsRedirectUri}&redirect_uri=${adfsRedirectUri}&state=${n}`;
        this.router.navigate([ROUTE.TOKEN]).then(() => {
            window.location.href = href;
        });
    }
    /** Returns the fullname of the current user. **/
    get principalName() {
        const token = this.token;
        if (token) {
            const decoded = jwt_decode(token);
            const voornaam = decoded['vo-voornaam'];
            const naam = decoded['sn'];
            if (voornaam && naam) {
                return `${voornaam} ${naam}`;
            }
        }
        return null;
    }
    /** Returns the voId of the current user. **/
    get voId() {
        const token = this.token;
        if (token) {
            const decoded = jwt_decode(token);
            const voId = decoded['vo-idv'];
            return voId;
        }
        return null;
    }
    /** Returns human readable information about the current logged in user, provided there is an autthenticated session*/
    get gebruiker() {
        if (this.hasAuthenticatedSession) {
            const decodedToken = jwt_decode(this.token);
            return this.gebruikerMapper.mapTokenToGebruiker(decodedToken);
        }
        return null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class AdfsAuthTokenComponent {
    constructor() {
        this.configurationService = inject(ConfigurationService);
        this.authService = inject(AdfsAuthService);
        this.location = inject(Location);
        this.subs = new Subscription();
    }
    ngOnInit() {
        const url = window.location.href, code = 'code';
        if (url.indexOf(code) !== -1) {
            const param = this.getParameterByName(code, url);
            // do post to get access token from adfs
            this.getAccessToken(param);
        }
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    getParameterByName(name, url) {
        if (!url) {
            url = window.location.href;
        }
        name = name.replace(/[\[\]]/g, '\\$&');
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'), results = regex.exec(url);
        if (!results) {
            return null;
        }
        if (!results[2]) {
            return '';
        }
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }
    getAccessToken(code) {
        const token$ = this.configurationService.getToken(code);
        const sub = token$.subscribe(({ accessToken }) => {
            this.authService.token = accessToken;
            if (!this.authService.token) {
                console.error('We hebben geen geldige token teruggekregen van ADFS.');
            }
            if (!this.authService.redirectUrl) {
                console.error('We hebben geen url om naar terug te gaan.');
            }
            else {
                // ga terug naar de oorspronkelijke pagina
                window.location.href = this.location.prepareExternalUrl(this.authService.redirectUrl);
            }
        });
        this.subs.add(sub);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthTokenComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AdfsAuthTokenComponent, selector: "ng-component", ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthTokenComponent, decorators: [{
            type: Component,
            args: [{ template: '' }]
        }] });

class AnbLoggedoutComponent {
    constructor(authService) {
        this.authService = authService;
    }
    ngOnInit() {
        this.authService.clear();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbLoggedoutComponent, deps: [{ token: AnbAuthenticationService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbLoggedoutComponent, isStandalone: true, selector: "anb-auth-logged-out", ngImport: i0, template: "<div class=\"vl-region\">\n  <div class=\"vl-layout vl-layout--wide\">\n    <div class=\"vl-grid vl-grid--is-stacked\">\n      <div class=\"vl-col--9-12 vl-col--1-1--xs\">\n        <!-- Begin Content  -->\n        <div class=\"vl-alert vl-alert--succes\" role=\"alert\">\n          <div class=\"vl-alert__icon\">\n            <i class=\"vl-vi vl-vi-warning\" aria-hidden=\"true\"></i>\n          </div>\n          <div class=\"vl-alert__content\">\n            <div class=\"vl-alert__message\">\n              <p>U bent succesvol afgemeld</p>\n              Sluit alle tabbladen en vensters van uw browser om zeker te zijn dat uw aanmeldgegevens gewist zijn\n              <!--TODO Beter bericht -->\n            </div>\n          </div>\n        </div>\n\n        <!-- End Content -->\n      </div>\n      <div class=\"vl-col--3-12 vl-col--3-12--m vl-col--12-12--s vl-col--1-1--xs\">\n        <!-- Begin Side navigation -->\n\n        <!-- End Side navigation -->\n      </div>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "ngmodule", type: CommonModule }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbLoggedoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-auth-logged-out', standalone: true, imports: [CommonModule], template: "<div class=\"vl-region\">\n  <div class=\"vl-layout vl-layout--wide\">\n    <div class=\"vl-grid vl-grid--is-stacked\">\n      <div class=\"vl-col--9-12 vl-col--1-1--xs\">\n        <!-- Begin Content  -->\n        <div class=\"vl-alert vl-alert--succes\" role=\"alert\">\n          <div class=\"vl-alert__icon\">\n            <i class=\"vl-vi vl-vi-warning\" aria-hidden=\"true\"></i>\n          </div>\n          <div class=\"vl-alert__content\">\n            <div class=\"vl-alert__message\">\n              <p>U bent succesvol afgemeld</p>\n              Sluit alle tabbladen en vensters van uw browser om zeker te zijn dat uw aanmeldgegevens gewist zijn\n              <!--TODO Beter bericht -->\n            </div>\n          </div>\n        </div>\n\n        <!-- End Content -->\n      </div>\n      <div class=\"vl-col--3-12 vl-col--3-12--m vl-col--12-12--s vl-col--1-1--xs\">\n        <!-- Begin Side navigation -->\n\n        <!-- End Side navigation -->\n      </div>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: AnbAuthenticationService }]; } });

class AdfsAuthGuardService extends AnbAuthGuardService {
    constructor() {
        super(...arguments);
        this.configurationService = inject(ConfigurationService);
        this.getSecurityPolicies = () => this.configurationService.getSecurityPolicies();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthGuardService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthGuardService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class AdfsClaimsComponent {
    constructor() {
        this.authService = inject(AdfsAuthService);
        this.claims = jwt_decode(this.authService.token);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsClaimsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AdfsClaimsComponent, isStandalone: true, selector: "anb-claims", providers: [AdfsAuthService], ngImport: i0, template: `<pre>{{ claims | json }}</pre>`, isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "pipe", type: i1.JsonPipe, name: "json" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsClaimsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'anb-claims',
                    standalone: true,
                    imports: [CommonModule],
                    providers: [AdfsAuthService],
                    template: `<pre>{{ claims | json }}</pre>`,
                }]
        }] });

const routes$2 = [
    { path: ROUTE.CLAIMS, component: AdfsClaimsComponent },
    { path: ROUTE.TOKEN, component: AdfsAuthTokenComponent },
    { path: ROUTE.LOGGEDOUT, component: AnbLoggedoutComponent },
];
class AdfsAuthModule {
    static forRoot(configurationFactory, configurationService) {
        return {
            ngModule: AdfsAuthModule,
            providers: [
                { provide: AuthConfiguration, useValue: AdfsConfiguration },
                { provide: AdfsConfiguration, useFactory: configurationFactory, deps: [ConfigurationService] },
                { provide: HTTP_INTERCEPTORS, useClass: AnbUnauthorizedHttpInterceptor, multi: true },
                { provide: HTTP_INTERCEPTORS, useClass: AnbAuthTokenHttpInterceptor, multi: true },
                { provide: ConfigurationService, useClass: configurationService },
                { provide: AnbAuthenticationService, useClass: AdfsAuthService, deps: [ConfigurationService] },
                { provide: AnbAuthGuardService, useClass: AdfsAuthGuardService },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthModule, imports: [i1$1.RouterModule, AdfsClaimsComponent, AnbLoggedoutComponent], exports: [RouterModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthModule, imports: [RouterModule.forChild(routes$2), AdfsClaimsComponent, AnbLoggedoutComponent, RouterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AdfsAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [RouterModule.forChild(routes$2), AdfsClaimsComponent, AnbLoggedoutComponent],
                    exports: [RouterModule],
                }]
        }] });

class AcmidmConfiguration extends AuthConfiguration {
}

class AcmidmApiAuthClient {
    constructor() {
        this.http = inject(HttpClient);
        this.configuration = inject(AcmidmConfiguration);
        this.getToken = () => this.http.get(this.configuration.tokenUrl, {
            withCredentials: true,
            responseType: 'text',
        });
        this.getPolicies = () => this.http.get(this.configuration.policiesUrl);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmApiAuthClient, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmApiAuthClient }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmApiAuthClient, decorators: [{
            type: Injectable
        }] });

class AcmIdmGebruikerMapper extends GebruikerMapper {
    mapTokenToGebruiker(token) {
        const rijksregisternummer = token['rrn'];
        const achternaam = token['family_name'];
        const voornaam = token['given_name'];
        const doelgroepcode = token['vo_doelgroepcode'];
        const doelgroepNaam = token['vo_doelgroepnaam'];
        const voId = token['vo_id'];
        const organisatieCode = token['vo_orgcode'];
        const organisatieNaam = token['vo_orgnaam'];
        const email = this.removeOrganisationSuffix(token['vo_email']);
        const rollen = this.normalizeRollen(token['anb_meerderetoepassingen_rol_3d']);
        const wetVer = token['wv_orgcode'] ?? '';
        const isWettelijkVertegenwoordiger = !!wetVer && wetVer.startsWith('WettelijkVertegenwoordiger');
        return new Gebruiker({
            achternaam: achternaam,
            voornaam: voornaam,
            rijksregisternummer: rijksregisternummer,
            email: email,
            voId: voId,
            organisatieCode: organisatieCode,
            organisatieNaam: organisatieNaam,
            rollen: rollen,
            isWettelijkVertegenwoordiger: isWettelijkVertegenwoordiger,
            doelgroepCode: doelgroepcode,
            doelgroepNaam: doelgroepNaam,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmIdmGebruikerMapper, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmIdmGebruikerMapper, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmIdmGebruikerMapper, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const TOKEN_KEY = 'Authentication::Token';
const POLICIES_KEY = 'Authorization::Policies';
class AcmidmAuthService extends AnbAuthenticationService {
    constructor() {
        super({ tokenKey: TOKEN_KEY, policiesKey: POLICIES_KEY });
        this.router = inject(Router);
        this.locationStrategy = inject(LocationStrategy);
        this.configuration = inject(AcmidmConfiguration);
        this.apiAuthClient = inject(AcmidmApiAuthClient);
        this.gebruikerMapper = inject(AcmIdmGebruikerMapper);
        /** Gets a new JWT token from the backend. **/
        this.getTokenFromServer = () => this.apiAuthClient.getToken().pipe(tap((token) => {
            this.token = token;
            this.policies = null;
        }), catchError(() => {
            return of(null);
        }));
        this.keySuffix = this.configuration.appName;
    }
    get LoggedoutPath() {
        return this.addBaseHrefToRedirect(ROUTE.LOGGEDOUT);
    }
    /** Returns the security policies for the logged in user. Ex. ['PARTIJ/U', 'PARTIJ/C'] **/
    getSecurityPolicies() {
        if (!this.policies?.length) {
            return this.apiAuthClient.getPolicies().pipe(tap((policies) => {
                this.policies = policies;
            }));
        }
        return of(this.policies);
    }
    /**
     * If the current JWT token in storage is still recent enough return it.
     * If the token is not recent but still valid refresh it without need for user consent.
     */
    getToken() {
        if (this.hasAuthenticatedSession && this.isTokenHalfLife()) {
            return this.getTokenFromServer();
        }
        return of(this.token);
    }
    /**
     * Return the url to the login page with the correct redirection (through token component and back to where we started)
     * Use this method to provide the login url that will be called by the auth guard or by the login button on your app
     */
    getLoginUrlWithRedirect(redirectPath = '/') {
        // path to the the token component includes the link that we must redirect to again once we have fetched the jwt token
        // if we are currently on the logged out page we redirect to the root instead of redirecting back to the logged out component
        redirectPath = this.addBaseHrefToRedirect(redirectPath);
        if (redirectPath === this.LoggedoutPath)
            redirectPath = this.baseHref;
        const tokenComponentPath = `${this.getBaseUrl()}${ROUTE.TOKEN}?redirectpath=${redirectPath}`;
        // we go to the authorization url and redirect to the token component once we are authenticated.
        // there we can fetch the token // and finally return to the path we were before the authorization flow started.
        return this.configuration.loginUrl + '?redirecturl=' + encodeURI(tokenComponentPath);
    }
    addBaseHrefToRedirect(redirectPath) {
        if (!!!redirectPath || redirectPath === '/')
            return this.baseHref;
        if (redirectPath.startsWith(this.baseHref))
            return redirectPath;
        const newRedirectPath = this.baseHref + redirectPath;
        return newRedirectPath.replace('//', '/');
    }
    /** Redirect the user to de ACM/IDM login page. **/
    redirectToLogin(path = '/') {
        this.redirectUrl = path;
        window.location.href = this.getLoginUrlWithRedirect(path);
    }
    redirectToAccessDenied() {
        this.router.navigate([this.configuration.accessDeniedPath]);
    }
    /** Returns the fullname of the current user. */
    get principalName() {
        if (this.token) {
            const decoded = jwt_decode(this.token);
            const voornaam = decoded['given_name'];
            const naam = decoded['family_name'];
            if (voornaam && naam) {
                return `${voornaam} ${naam}`;
            }
        }
        return null;
    }
    /** Returns the voId of the current user. */
    get voId() {
        if (this.token) {
            const decoded = jwt_decode(this.token);
            const voId = decoded['vo_id'];
            return voId;
        }
        return null;
    }
    /** Returns true when we passed the token halife it's half life. Use haseAuthenitacetedSession first. to know if our session is valid **/
    isTokenHalfLife() {
        if (!this.token) {
            return false;
        }
        const decoded = jwt_decode(this.token);
        const lifeTime = decoded.exp - decoded.iat;
        const halfLifeTime = new Date(0);
        halfLifeTime.setUTCSeconds(decoded.exp - lifeTime / 2); // TODO REMOVE ESLINT RULE that forbids expra paranthesis exp - (lifetiem/2) is clearer even if you know the order of precedence
        return halfLifeTime.valueOf() <= new Date().valueOf();
    }
    /** Base href must begin with a / per documentation */
    get baseHref() {
        return this.locationStrategy.getBaseHref();
    }
    getBaseUrl() {
        let url = `${window.location.protocol}//${window.location.hostname}`;
        if (window.location.port) {
            url += `:${window.location.port}`;
        }
        const baseUrl = url + this.baseHref;
        return baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    }
    /** Returns human readable information about the current logged in user, provided there is an autthenticated session*/
    get gebruiker() {
        if (this.hasAuthenticatedSession) {
            const decodedToken = jwt_decode(this.token);
            return this.gebruikerMapper.mapTokenToGebruiker(decodedToken);
        }
        return null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmAuthService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmAuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmAuthService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class AcmidmTokenComponent {
    constructor() {
        this.authService = inject(AcmidmAuthService);
        this.route = inject(ActivatedRoute);
        this.subs = new Subscription();
    }
    ngOnInit() {
        const sub = this.authService.getTokenFromServer().subscribe(() => {
            const redirectPath = this.route.snapshot.queryParamMap.get('redirectpath');
            const decodedRedirectPath = decodeURI(redirectPath);
            window.location.href = decodedRedirectPath;
        });
        this.subs.add(sub);
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmTokenComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AcmidmTokenComponent, selector: "ng-component", ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmTokenComponent, decorators: [{
            type: Component,
            args: [{ template: '' }]
        }] });

class AcmidmAuthGuardService extends AnbAuthGuardService {
    constructor() {
        super(...arguments);
        this.getSecurityPolicies = () => this.authService.getSecurityPolicies();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmAuthGuardService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmAuthGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmAuthGuardService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class AcmidmClaimsComponent {
    constructor() {
        this.authService = inject(AcmidmAuthService);
        this.claims$ = this.authService.getToken().pipe(map((token) => jwt_decode(token)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmClaimsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AcmidmClaimsComponent, isStandalone: true, selector: "anb-claims", providers: [AcmidmAuthService], ngImport: i0, template: `<pre>{{ claims$ | async | json }}</pre>`, isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.JsonPipe, name: "json" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmClaimsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'anb-claims',
                    standalone: true,
                    imports: [CommonModule],
                    providers: [AcmidmAuthService],
                    template: `<pre>{{ claims$ | async | json }}</pre>`,
                }]
        }] });

const routes$1 = [
    { path: ROUTE.CLAIMS, component: AcmidmClaimsComponent },
    { path: ROUTE.TOKEN, component: AcmidmTokenComponent },
    { path: ROUTE.LOGGEDOUT, component: AnbLoggedoutComponent },
];
class AcmidmAuthModule {
    static forRoot(configurationFactory) {
        return {
            ngModule: AcmidmAuthModule,
            providers: [
                { provide: AuthConfiguration, useValue: AcmidmConfiguration },
                { provide: AcmidmConfiguration, useFactory: configurationFactory },
                { provide: HTTP_INTERCEPTORS, useClass: AnbAuthTokenHttpInterceptor, multi: true },
                { provide: HTTP_INTERCEPTORS, useClass: AnbUnauthorizedHttpInterceptor, multi: true },
                { provide: AcmidmApiAuthClient },
                { provide: AnbAuthenticationService, useClass: AcmidmAuthService },
                { provide: AnbAuthGuardService, useClass: AcmidmAuthGuardService },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AcmidmAuthModule, imports: [CommonModule, i1$1.RouterModule, AcmidmClaimsComponent, AnbLoggedoutComponent], exports: [RouterModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmAuthModule, imports: [CommonModule, RouterModule.forChild(routes$1), AcmidmClaimsComponent, AnbLoggedoutComponent, RouterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AcmidmAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule, RouterModule.forChild(routes$1), AcmidmClaimsComponent, AnbLoggedoutComponent],
                    exports: [RouterModule],
                }]
        }] });

class CognitoClaimsComponent {
    constructor() {
        this.authService = inject(CognitoAuthService);
        this.claims$ = this.authService.getToken();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoClaimsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CognitoClaimsComponent, isStandalone: true, selector: "anb-cognito-claims", providers: [CognitoAuthService], ngImport: i0, template: `<pre>{{ claims$ | async | json }}</pre>`, isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.JsonPipe, name: "json" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoClaimsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'anb-cognito-claims',
                    standalone: true,
                    imports: [CommonModule],
                    providers: [CognitoAuthService],
                    template: `<pre>{{ claims$ | async | json }}</pre>`,
                }]
        }] });

const routes = [
    { path: ROUTE.CLAIMS, component: CognitoClaimsComponent },
    { path: ROUTE.LOGGEDOUT, component: AnbLoggedoutComponent },
];
class CognitoAuthModule {
    static forRoot(configurationFactory) {
        return {
            ngModule: CognitoAuthModule,
            providers: [
                { provide: AuthConfiguration, useValue: CognitoConfiguration },
                { provide: CognitoConfiguration, useFactory: configurationFactory },
                { provide: HTTP_INTERCEPTORS, useClass: AnbUnauthorizedHttpInterceptor, multi: true },
                { provide: HTTP_INTERCEPTORS, useClass: AnbCognitoAuthTokenHttpInterceptor, multi: true },
                { provide: CognitoApiAuthClient },
                { provide: AnbAuthenticationService, useClass: CognitoAuthService },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: CognitoAuthModule, imports: [CommonModule, i1$1.RouterModule, CognitoClaimsComponent, AnbLoggedoutComponent], exports: [RouterModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoAuthModule, imports: [CommonModule, RouterModule.forChild(routes), CognitoClaimsComponent, AnbLoggedoutComponent, RouterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CognitoAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule, RouterModule.forChild(routes), CognitoClaimsComponent, AnbLoggedoutComponent],
                    exports: [RouterModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AcmidmAuthModule, AcmidmAuthService, AcmidmConfiguration, AdfsAuthModule, AdfsAuthService, AdfsConfiguration, AnbAuthGuard, AnbAuthTokenHttpInterceptor, AnbAuthenticationService, AnbCognitoAuthGuard, AnbCognitoAuthTokenHttpInterceptor, AnbCognitoPermissionGuard, AnbModuleAuthGuard, AnbUnauthorizedHttpInterceptor, CognitoAuthModule, CognitoAuthService, CognitoConfiguration, ConfigurationService, Doelgroepen, Gebruiker, Permission, TaskPermission };
